import React, { ComponentProps, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import Link from "next/link";

import getStrainMatch from "api/requests/consumerApi/getStrainMatch";
import { Action } from "constants/events";
import { Strain } from "custom-types/Strain";
import { useEventTracker } from "hooks/useEventTracker";
import useUserFavorites from "hooks/useUserFavorites";
import { useSelector } from "redux/hooks";
import { getUserStrainPreferences } from "redux/selectors/user";
import { sortTopCannabs } from "utils/sortTopCannabs";
import { extractEffects } from "utils/strains/extractEffects";
import { extractTopTerp } from "utils/strains/extractTopTerp";

import FavoriteButton from "components/botanic/FavoriteButton";
import StarRating from "components/botanic/StarRating";
import EffectIcon from "components/Icons/EffectIcon";
import TerpeneIcon from "components/Icons/terpene.svg";
import Image from "components/Image";
import { Sizes } from "components/Image/Image";
import StrainMatchLabel from "components/StrainQuiz/StrainMatchLabel";

type Props = {
  /**
   * Custom accessory tray
   */
  accessoryTray?: React.ReactNode;
  className?: string;
  /**
   * Toggle whether the image should lazyload
   */
  disableLazyLoad?: boolean;
  /**
   * Optional href to override the default /strains/:slug, helpful for setting a full url path including hostname
   */
  href?: ComponentProps<typeof Link>["href"];
  /**
   * Custom image sizes array
   */
  imageSizes?: Sizes;
  /**
   * Display card vertically or horizontally
   */
  orientation?:
    | "vertical"
    | "horizontal"
    | {
        desktop: "vertical" | "horizontal";
        mobile: "vertical" | "horizontal";
      };
  /**
   * Boolean value to set a min-height style on the contents of the card. Intended for cards in carousels.
   */
  preserveHeight?:
    | boolean
    | {
        desktop: boolean;
        mobile: boolean;
      };
  /**
   * String with dollar amount for the price per an eighth ounce of flower
   */
  pricePerEighth?: string;
  showDescription?: boolean;
  /**
   * Boolean value to render match score label
   */
  showMatchScore?: boolean;
  /**
   * Strain for card
   */
  strain: Strain;
  trackingCategory?: string;
  trackingLabel?: string;
  /**
   * Whether or not to use micro data in card.
   */
  useMicrodata?: boolean;
};

const StrainCard: React.FC<Props> = ({
  accessoryTray,
  className,
  disableLazyLoad = false,
  href,
  imageSizes,
  orientation = "vertical",
  preserveHeight,
  pricePerEighth,
  showDescription,
  showMatchScore = false,
  strain,
  trackingCategory = "strain_card",
  trackingLabel = strain.name,
  useMicrodata,
  ...others
}) => {
  const { publishEvent } = useEventTracker();
  const {
    favoritedStrains,
    favoriteStrain: onFavoriteClick,
    checkIfUserFollows,
    favoritesEnabled,
  } = useUserFavorites();
  const strainPreferences = useSelector(getUserStrainPreferences);
  const [strainMatch, setStrainMatch] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const doesUserFollow = useMemo(
    () => checkIfUserFollows(strain.slug),
    [favoritedStrains?.strains],
  );

  const onClickStrainCard = () => {
    publishEvent({
      action: Action.click,
      category: trackingCategory,
      label: trackingLabel,
      strainId: strain.id,
    });
  };

  const roundedStrainRating = Math.round(strain.averageRating * 10) / 10;

  const determineEffect = () => {
    if (strain.effects && Object.entries(strain.effects).length > 0) {
      return extractEffects(strain.effects)
        .slice(0, 1)
        .map(({ name }) => name)[0];
    } else if (strain.topEffect) {
      return strain.topEffect;
    } else {
      return null;
    }
  };
  const effect = determineEffect();

  const topTerp = extractTopTerp(strain.terps || strain.topTerps);

  const sortedTopCannabs = sortTopCannabs(strain?.cannabinoids).slice(0, 2);
  const phenotype = strain.phenotype;

  const isOrientationObject = typeof orientation === "object";
  const isVerticalOrMobileVertical =
    orientation === "vertical" ||
    (isOrientationObject && orientation.mobile === "vertical");
  const isHorizontalOrMobileHorizontal =
    orientation === "horizontal" ||
    (isOrientationObject && orientation.mobile === "horizontal");
  const isVerticalDesktop =
    isOrientationObject && orientation.desktop === "vertical";
  const isHorizontalDesktop =
    isOrientationObject && orientation.desktop === "horizontal";

  const isPreserveHeightObject = typeof preserveHeight === "object";
  const isPreserveHeightDesktop =
    isPreserveHeightObject && preserveHeight.desktop && !preserveHeight.mobile;
  const isPreserveHeightMobile =
    isPreserveHeightObject && preserveHeight.mobile && !preserveHeight.desktop;
  const isPreserveHeight = !isPreserveHeightObject && preserveHeight === true;

  useEffect(() => {
    if (showMatchScore) {
      setIsLoading(true);

      getStrainMatch(
        strain.slug,
        strainPreferences?.strainMatchQuery || "",
      ).then((data) => {
        setStrainMatch(data.match);
        setIsLoading(false);
      });
    }
  }, [showMatchScore, strain.slug, strainPreferences?.strainMatchQuery]);

  return (
    <div
      className={cx(
        "relative flex flex-col justify-between bg-white h-full shadow-low rounded",
        className,
      )}
      {...others}
    >
      <Link
        href={href || `/strains/${strain.slug}`}
        className={cx({
          "md:p-lg": isHorizontalDesktop,
          "md:p-md": isVerticalDesktop,
          "p-lg": isHorizontalOrMobileHorizontal,
          "p-md": isVerticalOrMobileVertical,
        })}
        onClick={onClickStrainCard}
        data-testid="strain-card"
      >
        <div
          className={cx("flex relative", {
            "flex-col": isVerticalOrMobileVertical,
            "flex-row": isHorizontalOrMobileHorizontal,
            "md:flex-col": isVerticalDesktop,
            "md:flex-row": isHorizontalDesktop,
          })}
        >
          {favoritesEnabled && (
            <FavoriteButton
              aria-label="Favorite strain"
              className="absolute top-0 right-0 z-50"
              data-testid="strain-card-favorite"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFavoriteClick({
                  doesUserFollow,
                  favoriteGaLabel: "favorite strain card",
                  strainId: strain.id,
                  unFavoriteGaLabel: "unfavorite strain card",
                });
              }}
              isFavorited={doesUserFollow}
            />
          )}
          <div
            className={cx("relative w-full", {
              "md:mr-sm md:max-w-[75px] md:mb-0": isHorizontalDesktop,
              "md:mx-auto md:mb-sm md:max-w-[215px]": isVerticalDesktop,
              "mr-sm max-w-[75px]": isHorizontalOrMobileHorizontal,
              "mx-auto mb-sm": isVerticalOrMobileVertical,
            })}
          >
            <Image
              src={(strain.nugImage || strain.stockNugImage) as string}
              alt={`Nug image for ${strain.name}`}
              sizes={imageSizes || [orientation === "horizontal" ? 75 : 215]}
              ratio="square"
              disableLazyLoad={disableLazyLoad}
            />
            {showMatchScore && !isLoading && (
              <StrainMatchLabel strainMatch={strainMatch} />
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <div
              className={cx({
                "md:min-h-0": isPreserveHeightMobile,
                "md:min-h-[28px]": isPreserveHeightDesktop,
                "min-h-[28px]": isPreserveHeight || isPreserveHeightMobile,
              })}
            >
              {!!phenotype && (
                <div className="inline-block font-bold text-xs bg-leafly-white py-xs px-sm rounded mr-xs">
                  {phenotype}
                </div>
              )}
            </div>
            <div className="my-xs">
              <div className="font-bold text-sm mb-xs">
                {useMicrodata ? (
                  <span itemProp="name">{strain.name}</span>
                ) : (
                  strain.name
                )}
              </div>
              <div
                className={cx("text-xs truncate-lines text-grey", {
                  "md:min-h-0": isPreserveHeightMobile,
                  "md:min-h-[20px]": isPreserveHeightDesktop,
                  "min-h-[20px]": isPreserveHeight || isPreserveHeightMobile,
                })}
              >
                {strain.subtitle}
              </div>
            </div>
            <div
              className={cx("mb-xs", {
                "md:min-h-0": isPreserveHeightMobile,
                "md:min-h-[20px]": isPreserveHeightDesktop,
                "min-h-[20px]": isPreserveHeight || isPreserveHeightMobile,
              })}
            >
              {strain.averageRating && strain.reviewCount > 0 ? (
                <StarRating
                  rating={roundedStrainRating}
                  ratingCount={strain.reviewCount}
                  className="text-xs"
                />
              ) : (
                strain.reviewCount === 0 && (
                  <div className="text-xs">No reviews submitted</div>
                )
              )}
            </div>
            <div className="flex mb-xs">
              {sortedTopCannabs.length > 0 &&
                sortedTopCannabs.map((cannab) => {
                  return (
                    <span className="mr-md text-xs" key={cannab.displayName}>
                      {cannab.displayName}{" "}
                      {cannab.percentile50 !== null &&
                      cannab.percentile50 >= 0 ? (
                        `${cannab.percentile50}%`
                      ) : (
                        <>&#8212;</>
                      )}
                    </span>
                  );
                })}
            </div>
            {topTerp || effect ? (
              <div className="text-xs flex flex-wrap">
                {effect ? (
                  <div className="flex">
                    <EffectIcon className="max-w-none mr-xs" effect={effect} />
                    <div className="capitalize mr-md">{effect}</div>
                  </div>
                ) : (
                  <div className="text-xs mr-md">No effects reported</div>
                )}
                {topTerp && (
                  <div className="flex">
                    <TerpeneIcon
                      width="8"
                      height="17"
                      className={`text-${topTerp.name} mr-xs max-w-none`}
                    />
                    <div className="capitalize">{topTerp.name}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs mr-md text-grey">
                No effects or terps reported
              </div>
            )}
            {pricePerEighth && (
              <div className="mt-xs">
                <div className="text-xs text-grey"> 1/8 oz. starting at </div>
                <div className="text-md font-bold"> {pricePerEighth} </div>
              </div>
            )}
          </div>
        </div>
        {showDescription &&
          strain.shortDescriptionPlain?.indexOf("strain info") === -1 && (
            <p
              className={cx("text-xs mt-sm mb-none", {
                "md:min-h-0": isPreserveHeightMobile,
                "md:min-h-[60px]": isPreserveHeightDesktop,
                "min-h-[60px]": isPreserveHeight || isPreserveHeightMobile,
              })}
            >
              {`${strain.shortDescriptionPlain?.split(".")[0]}.`}
            </p>
          )}
      </Link>
      {accessoryTray}
    </div>
  );
};

export default StrainCard;
